import React from 'react';

export type IconType =
  | 'check'
  | 'error'
  | 'warning'
  | 'info'
  | 'trash'
  | 'upload'
  | 'plus'
  | 'x'
  | 'document'
  | 'check-circle'
  | 'x-circle'
  | 'arrow-left'
  | 'download'
  | 'refresh'
  | 'menu'
  | 'chevron-down'
  | 'chat-bubble'
  | 'pencil'
  | 'monitor'
  | 'lightning';

export interface IconProps {
  type: IconType;
  size?: 'sm' | 'md' | 'lg';
  color?: string;
  className?: string;
  ariaLabel?: string;
}

const Icon: React.FC<IconProps> = ({
  type,
  size = 'md',
  color,
  className = '',
  ariaLabel
}) => {
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'w-4 h-4';
      case 'md':
        return 'w-5 h-5';
      case 'lg':
        return 'w-6 h-6';
    }
  };

  const baseProps = {
    className: `${getSizeClasses()} ${color || ''} ${className}`.trim(),
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24',
    xmlns: 'http://www.w3.org/2000/svg',
    'aria-hidden': ariaLabel ? undefined : true,
    'aria-label': ariaLabel,
    role: 'img'
  };

  const strokeProps = {
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 2
  };

  switch (type) {
    case 'check':
      return (
        <svg {...baseProps}>
          <path {...strokeProps} d="M5 13l4 4L19 7" />
        </svg>
      );
    
    case 'error':
      return (
        <svg {...baseProps}>
          <path {...strokeProps} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    
    case 'warning':
      return (
        <svg {...baseProps}>
          <path {...strokeProps} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      );
    
    case 'info':
      return (
        <svg {...baseProps}>
          <path {...strokeProps} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    
    case 'trash':
      return (
        <svg {...baseProps}>
          <path {...strokeProps} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      );
    
    case 'upload':
      return (
        <svg {...baseProps}>
          <path {...strokeProps} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 15l3-3m0 0l3 3m-3-3v12" />
        </svg>
      );
    
    case 'plus':
      return (
        <svg {...baseProps}>
          <path {...strokeProps} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      );
    
    case 'x':
      return (
        <svg {...baseProps}>
          <path {...strokeProps} d="M6 18L18 6M6 6l12 12" />
        </svg>
      );
    
    case 'document':
      return (
        <svg {...baseProps}>
          <path {...strokeProps} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    
    case 'check-circle':
      return (
        <svg {...baseProps} viewBox="0 0 20 20" fill="currentColor" stroke="none">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      );
    
    case 'x-circle':
      return (
        <svg {...baseProps} viewBox="0 0 20 20" fill="currentColor" stroke="none">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      );

    case 'arrow-left':
      return (
        <svg {...baseProps}>
          <path {...strokeProps} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
      );

    case 'download':
      return (
        <svg {...baseProps}>
          <path {...strokeProps} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );

    case 'refresh':
      return (
        <svg {...baseProps}>
          <path {...strokeProps} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      );

    case 'menu':
      return (
        <svg {...baseProps}>
          <path {...strokeProps} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      );

    case 'chevron-down':
      return (
        <svg {...baseProps}>
          <path {...strokeProps} d="M19 9l-7 7-7-7" />
        </svg>
      );

    case 'chat-bubble':
      return (
        <svg {...baseProps}>
          <path
            {...strokeProps}
            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
          />
        </svg>
      );

    case 'pencil':
      return (
        <svg {...baseProps}>
          <path
            {...strokeProps}
            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
          />
        </svg>
      );

    case 'monitor':
      return (
        <svg {...baseProps}>
          <path
            {...strokeProps}
            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      );

    case 'lightning':
      return (
        <svg {...baseProps}>
          <path {...strokeProps} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
  }
};

export default Icon;
